/**
 * Centralised React Query cache keys for every domain. One file so invalidation targets are
 * discoverable and collision-free across domains. Factories mirror each domain's resource shape.
 */

/**
 * Stale time for REFERENCE data — things an operator changes deliberately and rarely
 * (sites, catalogues, taxonomies), as opposed to telemetry or alarm state.
 *
 * The app's default is 30s, which is right for live data but means a site list is
 * re-fetched on nearly every mount even though it changes a few times a year. These
 * queries instead follow an INVALIDATION model: serve from cache indefinitely, and
 * refetch only when a mutation says the data actually changed (e.g. `invalidateFleet`
 * after create/update/delete). One cached copy is then the single source of truth for
 * every consumer, and a rename is reflected immediately rather than within 30s.
 *
 * NOT `Infinity`: a change made in ANOTHER session (or by another admin) would then
 * never appear in this tab until a reload, which is exactly the class of staleness
 * this file is trying to remove. An hour is long enough that normal navigation costs
 * no requests, short enough that a missed invalidation self-heals within a session.
 * Anything that must be live (health, telemetry, alarms) must NOT use this.
 */
export const STALE_REFERENCE_MS = 60 * 60_000;

export const userKeys = {
  all: ['users'] as const,
  me: ['users', 'me'] as const,
  list: (limit: number, offset: number) => ['users', 'list', limit, offset] as const,
  invite: (token: string) => ['invitation', token] as const,
  invitations: (limit: number, offset: number) => ['invitations', 'list', limit, offset] as const,
  roles: ['roles'] as const,
};

export const growRoomKeys = {
  all: ['grow-rooms'] as const,
  list: ['grow-rooms', 'list'] as const,
  one: (id: string) => ['grow-rooms', id] as const,
};

export const growRoomRackKeys = {
  all: ['grow-room-racks'] as const,
  list: ['grow-room-racks', 'list'] as const,
  one: (id: string) => ['grow-room-racks', id] as const,
};

export const growRoomRackLevelKeys = {
  all: ['grow-room-rack-levels'] as const,
  list: ['grow-room-rack-levels', 'list'] as const,
  one: (id: string) => ['grow-room-rack-levels', id] as const,
};

export const plumbingRoomKeys = {
  all: ['plumbing-rooms'] as const,
  list: ['plumbing-rooms', 'list'] as const,
  one: (id: string) => ['plumbing-rooms', id] as const,
};

export const plumbingRoomNodeKeys = {
  all: ['plumbing-room-nodes'] as const,
  list: ['plumbing-room-nodes', 'list'] as const,
  one: (id: string) => ['plumbing-room-nodes', id] as const,
};

export const mcKeys = {
  all: ['mc'] as const,
  list: ['mc', 'list'] as const,
  one: (id: string) => ['mc', id] as const,
  status: (id: string) => ['mc', id, 'status'] as const,
  statuses: ['mc', 'statuses'] as const,
  children: (id: string) => ['mc', id, 'children'] as const,
  history: (id: string) => ['mc', id, 'history'] as const,
  historyAll: ['mc', 'history'] as const,
};

// Kind-segmented so a grow-room and a plumbing-room sharing a code never collide (D-049).
export const iotKeys = {
  growRoomActuators: (room: string) => ['iot', 'grow-room', room, 'actuators'] as const,
  growRoomActuator: (room: string, act: string) => ['iot', 'grow-room', room, 'actuators', act] as const,
  growRoomSensors: (room: string) => ['iot', 'grow-room', room, 'sensors'] as const,
  growRoomSensor: (room: string, s: string) => ['iot', 'grow-room', room, 'sensors', s] as const,
  growRoomRackActuators: (room: string, rack: string) =>
    ['iot', 'grow-room', room, 'rack', rack, 'actuators'] as const,
  growRoomRackActuator: (room: string, rack: string, act: string) =>
    ['iot', 'grow-room', room, 'rack', rack, 'actuators', act] as const,
  growRoomRackSensors: (room: string, rack: string) =>
    ['iot', 'grow-room', room, 'rack', rack, 'sensors'] as const,
  growRoomRackSensor: (room: string, rack: string, s: string) =>
    ['iot', 'grow-room', room, 'rack', rack, 'sensors', s] as const,
  plumbingRoomActuators: (room: string) => ['iot', 'plumbing-room', room, 'actuators'] as const,
  plumbingRoomActuator: (room: string, act: string) =>
    ['iot', 'plumbing-room', room, 'actuators', act] as const,
  plumbingRoomSensors: (room: string) => ['iot', 'plumbing-room', room, 'sensors'] as const,
  plumbingRoomSensor: (room: string, s: string) => ['iot', 'plumbing-room', room, 'sensors', s] as const,
  plumbingRoomNodeActuators: (room: string, node: string) =>
    ['iot', 'plumbing-room', room, 'node', node, 'actuators'] as const,
  plumbingRoomNodeActuator: (room: string, node: string, act: string) =>
    ['iot', 'plumbing-room', room, 'node', node, 'actuators', act] as const,
  plumbingRoomNodeSensors: (room: string, node: string) =>
    ['iot', 'plumbing-room', room, 'node', node, 'sensors'] as const,
  plumbingRoomNodeSensor: (room: string, node: string, s: string) =>
    ['iot', 'plumbing-room', room, 'node', node, 'sensors', s] as const,
};

// F6: a room's automation plan + settings (`/v1/production/plans`). Segmented
// by room so a mode/lights-on write invalidates only that room's cache, not
// every room's (the list key still exists for the "every room with a plan" read).
export const roomPlanKeys = {
  all: ['room-plans'] as const,
  list: ['room-plans', 'list'] as const,
  one: (room: string) => ['room-plans', room] as const,
  settings: (room: string) => ['room-plans', room, 'settings'] as const,
};

export const siteKeys = {
  all: ['sites'] as const,
  list: ['sites', 'list'] as const,
  one: (id: string) => ['sites', id] as const,
  manage: ['sites', 'manage'] as const,
};

export const serverKeys = {
  all: ['servers'] as const,
  list: ['servers', 'list'] as const,
};
